import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Loader2, ChevronUp, ChevronDown, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  type IntakeFieldDraft,
  type IntakeFieldType,
  getIntakeTemplateWithFields,
  saveIntakeTemplate,
} from '../../lib/crmIntakeFormTemplates';

const FIELD_TYPES: { value: IntakeFieldType; label: string }[] = [
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'single_select', label: 'Single choice' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'photo', label: 'Photo upload' },
];

function newFieldRow(): IntakeFieldDraft & { key: string } {
  return {
    key: crypto.randomUUID(),
    field_type: 'short_text',
    label: '',
    is_required: false,
    optionsText: '',
  };
}

function newSectionDraft(): { key: string; title: string; fields: Array<IntakeFieldDraft & { key: string }> } {
  return {
    key: crypto.randomUUID(),
    title: '',
    fields: [],
  };
}

interface CreateIntakeFormTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  templateId: string | null;
  onSaved: () => void;
}

export default function CreateIntakeFormTemplateModal({
  isOpen,
  onClose,
  businessId,
  templateId,
  onSaved,
}: CreateIntakeFormTemplateModalProps) {
  const [title, setTitle] = useState('');
  const [sections, setSections] = useState<
    Array<{ key: string; title: string; fields: Array<IntakeFieldDraft & { key: string }> }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const resetBlank = useCallback(() => {
    setTitle('');
    setSections([]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    async function load() {
      if (!templateId) {
        resetBlank();
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getIntakeTemplateWithFields(templateId);
        if (cancelled || !data) {
          if (!data && !cancelled) toast.error('Form not found');
          return;
        }
        setTitle(data.template.title);
        if (data.sections.length === 0) {
          setSections([]);
        } else {
          setSections(
            data.sections.map((s) => ({
              key: s.section.id,
              title: s.section.title,
              fields: s.fields.map((f) => ({
                key: f.id,
                field_type: f.field_type,
                label: f.label,
                is_required: f.is_required,
                optionsText: f.field_type === 'photo' ? (f.options[0] ?? '5') : f.options.join('\n'),
              })),
            })),
          );
        }
      } catch (e) {
        console.error(e);
        toast.error('Failed to load form');
        resetBlank();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, templateId, resetBlank]);

  const moveSection = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= sections.length) return;
    setSections((prev) => {
      const copy = [...prev];
      const t = copy[index];
      copy[index] = copy[next];
      copy[next] = t;
      return copy;
    });
  };

  const addSection = () => {
    setSections((prev) => [...prev, newSectionDraft()]);
  };

  const removeSection = (sectionKey: string) => {
    setSections((prev) => {
      const s = prev.find((x) => x.key === sectionKey);
      if (s?.fields.length && !window.confirm('This section has fields. Remove it anyway?')) return prev;
      return prev.filter((x) => x.key !== sectionKey);
    });
  };

  const addFieldToSection = (sectionKey: string) => {
    setSections((prev) =>
      prev.map((s) => (s.key === sectionKey ? { ...s, fields: [...s.fields, newFieldRow()] } : s)),
    );
  };

  const moveFieldInSection = (sectionKey: string, index: number, dir: -1 | 1) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s.key !== sectionKey) return s;
        const next = index + dir;
        if (next < 0 || next >= s.fields.length) return s;
        const fields = [...s.fields];
        const t = fields[index];
        fields[index] = fields[next];
        fields[next] = t;
        return { ...s, fields };
      }),
    );
  };

  const handleSave = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Give the form a title');
      return;
    }
    if (sections.length === 0) {
      toast.error('Add at least one section before saving');
      return;
    }
    setSaving(true);
    try {
      await saveIntakeTemplate({
        businessId,
        templateId,
        title: trimmed,
        sections: sections.map((s) => ({
          title: s.title.trim() || 'Section',
          fields: s.fields.map(({ key: _k, ...rest }) => rest),
        })),
      });
      toast.success(templateId ? 'Template updated' : 'Template saved');
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[100230] flex items-end sm:items-center justify-center sm:p-6"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl border border-gray-200/80 flex flex-col max-h-[92vh] sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 sm:px-6 sm:py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {templateId ? 'Edit intake template' : 'Create intake template'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add sections with titles, then add fields inside each section. Customers see each section as a group on the live form.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 active:scale-95 transition touch-manipulation"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Form title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. New lead — quick intake"
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sections &amp; fields</span>
                <button
                  type="button"
                  onClick={addSection}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 active:scale-[0.98] transition touch-manipulation"
                >
                  <Layers className="h-4 w-4" />
                  Add section
                </button>
              </div>

              {sections.length === 0 ? (
                <p className="text-sm text-gray-600 text-center py-10 border border-dashed border-gray-200 rounded-xl bg-gray-50/80 px-4">
                  No sections yet. Click <span className="font-semibold text-gray-800">Add section</span>, name it (e.g.
                  &quot;Client personal info&quot;), then use <span className="font-semibold text-gray-800">Add field</span> inside that
                  section.
                </p>
              ) : (
                <ul className="space-y-4">
                  {sections.map((sec, secIndex) => (
                    <li
                      key={sec.key}
                      className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04)] overflow-hidden"
                    >
                      <div className="flex items-start gap-2 px-3 py-3 sm:px-4 bg-gradient-to-r from-emerald-50/80 to-gray-50/80 border-b border-gray-100">
                        <div className="flex flex-col gap-0.5 pt-1">
                          <button
                            type="button"
                            disabled={secIndex === 0}
                            onClick={() => moveSection(secIndex, -1)}
                            className="p-1 rounded-md text-gray-500 hover:bg-white disabled:opacity-30"
                            aria-label="Move section up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={secIndex === sections.length - 1}
                            onClick={() => moveSection(secIndex, 1)}
                            className="p-1 rounded-md text-gray-500 hover:bg-white disabled:opacity-30"
                            aria-label="Move section down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <label className="text-[11px] font-semibold text-emerald-900/80 uppercase tracking-wide">
                            Section title
                          </label>
                          <input
                            type="text"
                            value={sec.title}
                            onChange={(e) =>
                              setSections((prev) =>
                                prev.map((s) => (s.key === sec.key ? { ...s, title: e.target.value } : s)),
                              )
                            }
                            placeholder="e.g. Client personal info"
                            className="w-full px-3 py-2 rounded-xl border border-emerald-100/80 text-sm font-medium text-gray-900 bg-white/90 focus:outline-none focus:ring-2 focus:ring-emerald-500/25 focus:border-emerald-400"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSection(sec.key)}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition flex-shrink-0"
                          aria-label="Remove section"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="px-3 py-3 sm:px-4 sm:py-4 space-y-3 bg-gray-50/40">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-medium text-gray-500">Fields in this section</span>
                          <button
                            type="button"
                            onClick={() => addFieldToSection(sec.key)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add field
                          </button>
                        </div>

                        {sec.fields.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-xl bg-white/60">
                            No fields in this section yet.
                          </p>
                        ) : (
                          <ul className="space-y-3">
                            {sec.fields.map((row, index) => (
                              <li
                                key={row.key}
                                className="rounded-xl border border-gray-200 bg-white p-3 space-y-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                              >
                                <div className="flex items-start gap-2">
                                  <div className="flex flex-col gap-0.5 pt-1">
                                    <button
                                      type="button"
                                      disabled={index === 0}
                                      onClick={() => moveFieldInSection(sec.key, index, -1)}
                                      className="p-1 rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                                      aria-label="Move field up"
                                    >
                                      <ChevronUp className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={index === sec.fields.length - 1}
                                      onClick={() => moveFieldInSection(sec.key, index, 1)}
                                      className="p-1 rounded-md text-gray-500 hover:bg-gray-50 disabled:opacity-30"
                                      aria-label="Move field down"
                                    >
                                      <ChevronDown className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <div className="flex-1 min-w-0 space-y-2">
                                    <div className="grid sm:grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-[11px] font-medium text-gray-500">Type</label>
                                        <select
                                          value={row.field_type}
                                          onChange={(e) => {
                                            const v = e.target.value as IntakeFieldType;
                                            setSections((prev) =>
                                              prev.map((s) =>
                                                s.key !== sec.key
                                                  ? s
                                                  : {
                                                      ...s,
                                                      fields: s.fields.map((r, i) =>
                                                        i === index
                                                          ? {
                                                              ...r,
                                                              field_type: v,
                                                              optionsText:
                                                                v === 'single_select'
                                                                  ? r.optionsText
                                                                  : v === 'photo'
                                                                    ? (r.field_type === 'photo' ? r.optionsText : '5')
                                                                    : '',
                                                            }
                                                          : r,
                                                      ),
                                                    },
                                              ),
                                            );
                                          }}
                                          className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white"
                                        >
                                          {FIELD_TYPES.map((ft) => (
                                            <option key={ft.value} value={ft.value}>
                                              {ft.label}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="text-[11px] font-medium text-gray-500">Label</label>
                                        <input
                                          type="text"
                                          value={row.label}
                                          onChange={(e) =>
                                            setSections((prev) =>
                                              prev.map((s) =>
                                                s.key !== sec.key
                                                  ? s
                                                  : {
                                                      ...s,
                                                      fields: s.fields.map((r, i) =>
                                                        i === index ? { ...r, label: e.target.value } : r,
                                                      ),
                                                    },
                                              ),
                                            )
                                          }
                                          placeholder="Question shown to customer"
                                          className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm"
                                        />
                                      </div>
                                    </div>
                                    {row.field_type === 'single_select' && (
                                      <div>
                                        <label className="text-[11px] font-medium text-gray-500">Choices (one per line)</label>
                                        <textarea
                                          value={row.optionsText}
                                          onChange={(e) =>
                                            setSections((prev) =>
                                              prev.map((s) =>
                                                s.key !== sec.key
                                                  ? s
                                                  : {
                                                      ...s,
                                                      fields: s.fields.map((r, i) =>
                                                        i === index ? { ...r, optionsText: e.target.value } : r,
                                                      ),
                                                    },
                                              ),
                                            )
                                          }
                                          rows={3}
                                          placeholder={'Option A\nOption B'}
                                          className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm resize-y min-h-[72px]"
                                        />
                                      </div>
                                    )}
                                    {row.field_type === 'photo' && (
                                      <div>
                                        <label className="text-[11px] font-medium text-gray-500">Max photos (1–20)</label>
                                        <input
                                          type="number"
                                          min={1}
                                          max={20}
                                          value={row.optionsText || '5'}
                                          onChange={(e) =>
                                            setSections((prev) =>
                                              prev.map((s) =>
                                                s.key !== sec.key
                                                  ? s
                                                  : {
                                                      ...s,
                                                      fields: s.fields.map((r, i) =>
                                                        i === index ? { ...r, optionsText: e.target.value } : r,
                                                      ),
                                                    },
                                              ),
                                            )
                                          }
                                          className="mt-0.5 w-full px-2 py-2 rounded-lg border border-gray-200 text-sm bg-white"
                                        />
                                        <p className="mt-1 text-[11px] text-gray-400">Customers can attach up to this many images (JPEG, PNG, WebP, HEIC).</p>
                                      </div>
                                    )}
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={row.is_required}
                                        onChange={(e) =>
                                          setSections((prev) =>
                                            prev.map((s) =>
                                              s.key !== sec.key
                                                ? s
                                                : {
                                                    ...s,
                                                    fields: s.fields.map((r, i) =>
                                                      i === index ? { ...r, is_required: e.target.checked } : r,
                                                    ),
                                                  },
                                            ),
                                          )
                                        }
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      />
                                      Required
                                    </label>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSections((prev) =>
                                        prev.map((s) =>
                                          s.key !== sec.key
                                            ? s
                                            : { ...s, fields: s.fields.filter((_, i) => i !== index) },
                                        ),
                                      )
                                    }
                                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                                    aria-label="Remove field"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 sm:px-6 border-t border-gray-100 flex gap-2 justify-end bg-white sm:rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || loading || sections.length === 0}
            onClick={handleSave}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
